import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { MoreHorizontal, Pencil } from 'lucide-react'
import CommonTable, { type TableColumn } from '@/components/common/CommonTable'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { RoutesLink } from '@/utility/RouteLinks'
import CommonFilter, { type IFilter } from '@/components/common/CommonFilter'
import { BranchService } from '@/services/Branch/BranchSevice'
import { ThrowError } from '@/api/callApi'
import { GetFilterUrl, showToastNotification } from '@/utility/helper'
import type { IPageInfo } from '@/interface/common.interface'
import type { branch } from '@/interface/branch.interface'
import TablePagination from '@/components/common/TablePagination'

const BranchSalaryHead: React.FC = () => {
    const navigate = useNavigate();
    const [branches, setBranches] = useState<branch[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState<IFilter>({
        search: "",
    });

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });

    const handleSearchChange = useCallback((value: string) => {
        setFilter((prev) => {
            if (prev.search === value) return prev;
            return { ...prev, search: value };
        });
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    }, []);

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
    };

    const handleGetBranch = async (
        currentPage = paginationData.currentPage,
        currentLimit = paginationData.pageSize,
    ) => {
        setIsLoading(true);
        try {
            const apiFilter = {
                ...filter,
            };
            const paramsURL = GetFilterUrl(apiFilter);
            const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${paramsURL ? `&${paramsURL}` : ""}`;
            const response = await BranchService?.getBranch(PATH);
            if (response?.status) {
                setBranches(response?.data?.branch || []);
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                }));
            } else {
                showToastNotification("danger", response?.message);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        handleGetBranch(paginationData.currentPage, paginationData.pageSize);
    }, [paginationData.currentPage, paginationData.pageSize, filter]);

    const columns: TableColumn<branch>[] = [
        {
            key: "#",
            header: "#",
            render: (_item, index) => {
                return (
                    <span className="text-muted-foreground font-medium text-sm">
                        {String(
                            (paginationData.currentPage - 1) * paginationData.pageSize +
                            index +
                            1,
                        ).padStart(2, "0")}
                    </span>
                )
            }
        },
        {
            key: "name",
            header: "Branch Name",
            render: (data: branch) => {
                return (
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-sm">
                            {data?.name?.charAt(0)?.toUpperCase() || "B"}
                        </div>
                        <span className="font-semibold capitalize text-foreground">{data?.name || "—"}</span>
                    </div>
                )
            }
        },
        {
            key: "aliasId",
            header: "Alias ID",
            render: (data: branch) => {
                return <span className="font-medium text-gray-700">{data?.aliasId || "—"}</span>
            }
        },
        {
            key: "phone",
            header: "Phone",
            render: (data: branch) => {
                return <span className="text-gray-600">{data?.phone || "—"}</span>
            }
        },
        {
            key: "email",
            header: "Email",
            render: (data: branch) => {
                return <span className="text-gray-600">{data?.email || "—"}</span>
            }
        },
        {
            key: "address",
            header: "Address",
            render: (data: branch) => {
                const parts = [data?.address, data?.city, data?.state, data?.pinCode].filter(Boolean);
                return <span className="text-gray-600 capitalize text-sm">{parts.join(", ") || "—"}</span>
            }
        },
        {
            key: "action",
            header: "Action",
            render: (item: branch) => {
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4 " />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl shadow-lg border-slate-100">
                            <DropdownMenuItem
                                onClick={() => navigate(`${RoutesLink.editBranchSalaryHead}/${item.id || ''}`)}
                                className=""
                            >
                                <Pencil className="h-4 w-4 " />Edit
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )
            }
        }
    ]

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading title='Branch salary head'
                    //  buttonText='Add branch salary head'
                    //  buttonIcon={<Plus />}
                    //     onClick={() => navigate(RoutesLink.addBranchSalaryHead)}
                    isBackButtonVisible={true}
                />

                <div className="space-y-4">
                    <CommonFilter
                        searchValue={filter.search || ""}
                        onSearchChange={handleSearchChange}
                    />
                    <CommonTable columns={columns} data={branches} loading={isLoading} />
                    <TablePagination
                        paginationData={paginationData}
                        setPaginationData={setPaginationData}
                        handleChangePageSize={handleChangePageSize}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </Layout>
    )
}

export default BranchSalaryHead
